import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from 'src/component/ds/utils';

/* "Button - Vega" in Figma. States map to pseudo-classes and aria-invalid. */
const buttonVariants = cva(
  [
    'ds-inline-flex ds-items-center ds-justify-center ds-whitespace-nowrap',
    'ds-rounded-full ds-font-medium',
    'ds-transition-colors',
    'focus-visible:ds-outline-none focus-visible:ds-shadow-ds-focus',
    'disabled:ds-pointer-events-none disabled:ds-opacity-50',
    'aria-[invalid=true]:ds-shadow-ds-focus-error',
    '[&_svg]:ds-pointer-events-none [&_svg]:ds-shrink-0'
  ],
  {
    variants: {
      variant: {
        primary: 'ds-bg-primary ds-text-primary-foreground hover:ds-bg-steel-600 active:ds-bg-steel-700',
        secondary: 'ds-bg-secondary ds-text-secondary-foreground hover:ds-bg-steel-300 active:ds-bg-steel-400',
        destructive: 'ds-bg-destructive ds-text-destructive-foreground hover:ds-opacity-90 active:ds-opacity-100',
        outline: 'ds-border ds-border-border ds-bg-background ds-text-foreground hover:ds-bg-muted active:ds-bg-secondary',
        ghost: 'ds-bg-transparent ds-text-foreground hover:ds-bg-muted active:ds-bg-secondary',
        link: 'ds-bg-transparent ds-text-foreground ds-underline-offset-4 hover:ds-underline'
      },
      size: {
        xs: 'ds-h-ds-xs ds-px-2 ds-py-[3px] ds-gap-1.5 ds-text-ds-mini',
        sm: 'ds-h-ds-sm ds-px-2.5 ds-py-1.5 ds-gap-1.5 ds-text-ds-sm',
        default: 'ds-h-ds-md ds-px-2.5 ds-py-2 ds-gap-2 ds-text-ds-sm',
        lg: 'ds-h-ds-lg ds-px-2.5 ds-py-2.5 ds-gap-2 ds-text-ds-base'
      }
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default'
    }
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  /** Render the child element instead of a <button>, keeping the styling. */
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, type = 'button', ...props }, ref) => {
    return (
      <button
        ref={ref}
        type={type}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
